import React, { useState } from 'react';
import { TrendingUp, TrendingDown, Target, MessageSquare, ShoppingBag, Clock, Flame, Trophy, Coins, Pencil, Check } from 'lucide-react';

const PERIODS = [['yesterday', 'Hier'], ['today', "Aujourd'hui"], ['7d', '7 jours'], ['14d', '14 jours'], ['30d', '30 jours'], ['all', 'Tout']];

// Reusable performance dashboard. Used by the chatter's own "Mes stats" page (editable
// goal) and the Employee Report click-through (read-only, scoped to one employee).
export default function StatsDashboard({ data, period, onPeriod, editable, onSetGoal, onAttach }) {
  const m = data || {};
  const prog = m.progression;
  const daily = m.daily || [];
  const maxRev = Math.max(1, ...daily.map(d => d.revenue || 0));
  const goalVal = m.weekly_goal_value;
  const goalType = m.weekly_goal_type || 'revenue';
  const weekDone = m.week_done || 0;
  const goalPct = goalVal ? Math.min(100, Math.round((100 * weekDone) / goalVal)) : 0;

  return (
    <div className="space-y-3" data-testid="stats-dashboard">
      {onPeriod && (
        <div className="flex gap-1.5 flex-wrap">
          {PERIODS.map(([v, l]) => (
            <button key={v} data-testid={`sd-period-${v}`} onClick={() => onPeriod(v)}
              className={`px-3 h-8 rounded-lg text-xs font-medium transition-colors ${period === v ? 'bg-indigo-500 text-white' : 'bg-white/5 text-slate-300 hover:bg-white/10 border border-white/10'}`}>{l}</button>
          ))}
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-3">
        <div className="rounded-2xl border border-emerald-500/20 bg-gradient-to-br from-emerald-500/[0.08] to-transparent p-5">
          <div className="text-xs text-slate-400 flex items-center gap-1.5"><Coins size={13} className="text-emerald-400" /> Ventes validées</div>
          <div className="text-3xl font-bold text-white mt-1" data-testid="sd-revenue">{(m.total_sales || 0).toLocaleString()} €</div>
          <div className="flex items-center gap-2 mt-2">
            {prog != null && (
              <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-md ${prog >= 0 ? 'bg-emerald-500/15 text-emerald-300' : 'bg-rose-500/15 text-rose-300'}`}>
                {prog >= 0 ? <TrendingUp size={12} /> : <TrendingDown size={12} />}{prog > 0 ? '+' : ''}{prog}%
              </span>
            )}
            <span className="text-[11px] text-slate-500">vs période précédente</span>
          </div>
          <div className="text-[11px] text-slate-400 mt-3 pt-3 border-t border-white/5">{m.sales_count || 0} ventes · panier moyen {m.avg_basket || 0} €</div>
        </div>
        <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-4">
          <div className="text-xs text-slate-400 mb-3">Revenu par jour (7 j)</div>
          <div className="flex items-end gap-1.5 h-[120px]">
            {daily.map((d, i) => (
              <div key={i} className="flex-1 flex flex-col items-center gap-1 justify-end h-full">
                <div className="w-full rounded-t-md bg-gradient-to-t from-sky-500 to-cyan-400" style={{ height: `${Math.max(4, Math.round((100 * (d.revenue || 0)) / maxRev))}%` }} title={`${d.revenue} €`} />
                <span className="text-[10px] text-slate-500">{d.label}</span>
              </div>
            ))}
            {daily.length === 0 && <div className="w-full text-center text-xs text-slate-600 self-center">Pas encore de données</div>}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-4 flex items-center gap-3">
          <Gauge pct={m.conversion_rate || 0} />
          <div>
            <div className="text-xs text-slate-400">Conversion</div>
            <div className="text-xl font-bold text-white">{m.conversion_rate || 0}%</div>
            <div className="text-[10px] text-slate-500">{m.conversations || 0} fans</div>
          </div>
        </div>
        <Tile icon={MessageSquare} color="text-indigo-400" label="Messages" value={(m.messages || 0).toLocaleString()} sub={`${m.payment_links || 0} liens de paiement`} />
        <Tile icon={Clock} color="text-amber-400" label="En attente" value={`${(m.pending_total || 0).toLocaleString()} €`} sub={`${m.pending_count || 0} à valider`} />
        <Tile icon={ShoppingBag} color="text-violet-400" label="Panier moyen" value={`${m.avg_basket || 0} €`} sub="par vente" />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Tile icon={Flame} color="text-orange-400" label="Série" value={`${m.streak || 0} j`} sub="avec vente" />
        <Tile icon={Trophy} color="text-amber-400" label="Meilleure vente" value={`${(m.best_sale || 0).toLocaleString()} €`} sub="record" />
        <div className="col-span-2 rounded-2xl border border-white/10 bg-white/[0.02] p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs text-slate-400 flex items-center gap-1.5"><Target size={13} className="text-cyan-400" /> Objectif de la semaine</span>
            <span className="text-xs font-semibold text-white" data-testid="sd-goal">{goalVal ? `${weekDone.toLocaleString()} / ${goalVal.toLocaleString()} ${goalType === 'revenue' ? '€' : 'ventes'}` : '—'}</span>
          </div>
          <div className="h-2.5 rounded-full bg-white/10 overflow-hidden"><div className="h-full bg-gradient-to-r from-cyan-400 to-fuchsia-500" style={{ width: `${goalPct}%` }} /></div>
          {editable && <GoalEditor current={goalVal} type={goalType} onSave={onSetGoal} />}
        </div>
      </div>

      <div className="rounded-2xl border border-white/10 bg-white/[0.02] overflow-hidden">
        <div className="px-4 py-2.5 text-sm font-semibold text-white border-b border-white/5">Ventes ({(m.sales || []).length})</div>
        <div className="max-h-[280px] overflow-y-auto scrollbar-thin">
          {(m.sales || []).map((s, i) => {
            const canAttach = onAttach && s.id && !s.conversation_id;
            return (
            <div key={i} className="flex items-center justify-between px-4 py-2 border-b border-white/5 text-sm">
              <span className="text-white truncate pr-2">{canAttach ? <span className="text-amber-300">{s.fan} · à rattacher</span> : s.fan}</span>
              <span className="flex items-center gap-3 shrink-0">
                <span className="text-[11px] text-slate-500 hidden sm:inline">{s.type}</span>
                <span className={`text-[10px] px-2 py-0.5 rounded-full ${s.status === 'approved' ? 'bg-emerald-500/15 text-emerald-300' : 'bg-amber-500/15 text-amber-300'}`}>{s.status}</span>
                <span className="font-bold text-white w-16 text-right">{s.amount} €</span>
                {canAttach && (
                  <button onClick={() => onAttach(s)} data-testid={`mystats-attach-${s.id}`}
                    className="text-[11px] font-semibold text-white rounded-lg px-2.5 py-1 shrink-0"
                    style={{ background: 'linear-gradient(135deg,#3f7dff,#73a6ff)' }}>
                    Rattacher
                  </button>
                )}
              </span>
            </div>
            );
          })}
          {(!m.sales || m.sales.length === 0) && <div className="px-4 py-8 text-center text-xs text-slate-500">Aucune vente sur cette période.</div>}
        </div>
      </div>
    </div>
  );
}

function Tile({ icon: Icon, color, label, value, sub }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-4">
      <div className="text-xs text-slate-400 flex items-center gap-1.5">{Icon && <Icon size={13} className={color} />} {label}</div>
      <div className="text-xl font-bold text-white mt-1">{value}</div>
      {sub && <div className="text-[10px] text-slate-500 mt-0.5">{sub}</div>}
    </div>
  );
}

function Gauge({ pct }) {
  const r = 22;
  const c = 2 * Math.PI * r;
  const off = c * (1 - Math.min(100, pct) / 100);
  return (
    <svg width="54" height="54" viewBox="0 0 54 54" className="shrink-0">
      <circle cx="27" cy="27" r={r} fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth="6" />
      <circle cx="27" cy="27" r={r} fill="none" stroke="#22d3ee" strokeWidth="6" strokeLinecap="round" strokeDasharray={c} strokeDashoffset={off} transform="rotate(-90 27 27)" />
    </svg>
  );
}

function GoalEditor({ current, type, onSave }) {
  const [open, setOpen] = useState(false);
  const [val, setVal] = useState(current || '');
  const [t, setT] = useState(type || 'revenue');
  if (!open) {
    return (
      <button data-testid="sd-edit-goal" onClick={() => setOpen(true)} className="mt-2.5 text-[11px] text-cyan-300 hover:text-cyan-200 flex items-center gap-1">
        <Pencil size={11} /> {current ? 'Modifier mon objectif' : 'Définir mon objectif'}
      </button>
    );
  }
  return (
    <div className="mt-3 flex items-center gap-2 flex-wrap">
      <input data-testid="sd-goal-input" type="number" value={val} onChange={e => setVal(e.target.value)} placeholder="ex. 1000" className="w-24 bg-white/5 border border-white/10 rounded-lg px-2 py-1.5 text-sm text-white outline-none" />
      <div className="flex rounded-lg border border-white/10 overflow-hidden text-xs">
        <button onClick={() => setT('revenue')} className={`px-2.5 py-1.5 ${t === 'revenue' ? 'bg-indigo-500 text-white' : 'text-slate-400'}`}>€</button>
        <button onClick={() => setT('sales')} className={`px-2.5 py-1.5 ${t === 'sales' ? 'bg-indigo-500 text-white' : 'text-slate-400'}`}>ventes</button>
      </div>
      <button data-testid="sd-goal-save" onClick={() => { onSave(val ? parseFloat(val) : null, t); setOpen(false); }} className="bg-cyan-500 hover:bg-cyan-600 text-white rounded-lg px-3 py-1.5 text-xs font-semibold flex items-center gap-1"><Check size={12} /> Ok</button>
    </div>
  );
}
